import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import ByzMuiSelect from 'src/components/core-components/mui/ByzMuiSelect';
import ByzMuiTextField from 'src/components/core-components/mui/inputs/ByzMuiTextField';
import ByzMuiSaveButton from 'src/components/core-components/mui/buttons/ByzMuiSaveButton';
import ByzMuiTypography from 'src/components/core-components/mui/inputs/ByzMuiTypography';
import { useBaseMutationWithVariables, useBaseQuery } from 'src/api/core/useBaseQuery';
import { useQueryClient } from '@tanstack/react-query';

interface LiquidOutModalProps {
  fbid: number
  ptId: number;
  onClose: () => void;
  onSave?: (data: any) => void;
}

const LiquidOutModal: React.FC<LiquidOutModalProps> = ({ fbid, ptId, onClose, onSave }) => {
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    outputId: '',
    description: '',
    amount: '',
    processTime: new Date().toISOString().slice(0, 16), // datetime-local format
  });

  const { mutate: createFluidOutput } = useBaseMutationWithVariables({
    endpoint: `/reference/api/Card/CreateFluidBalanceFluidOutput`,
    method: 'POST',
  });

  const { data: fluidOutputs, isLoading: outputsLoading } = useBaseQuery<any>({
    endpoint: `/reference/api/Card/GetMcFluidBalanceFluidOutput`,
    queryKey: ['fluidOutputs'],
    options: {
      enabled: true,
    }
  });

  const sanitizePositiveNumber = (value: string): string => {
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    const sanitized = parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : cleaned;
    const num = parseFloat(sanitized);
    if (isNaN(num) || num < 0) return '';
    return sanitized;
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'amount' ? sanitizePositiveNumber(value) : value
    }));
  };

  const toLocalISOString = (date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const handleSave = () => {
    const payload = {
      FbId: fbid,
      FId: Number(formData.outputId),
      Ptime: toLocalISOString(new Date(formData.processTime)),
      Amount: Number(formData.amount),
      Explanation: formData.description,
    };

    createFluidOutput(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['liquidOutData'] });
        queryClient.invalidateQueries({ queryKey: ['fluidTypesBalance'] });
        onClose();
      },
      onError: (error) => {
        console.error('Sıvı çıkışı kaydedilemedi:', error);
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Adı
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiSelect
              label="Adı"
              value={
                formData.outputId && fluidOutputs?.data
                  ? { 
                      id: formData.outputId,
                      value: formData.outputId,
                      label: fluidOutputs.data.find((o: any) => o.id === formData.outputId)?.name || ''
                    }
                  : null
              }
              onChange={(event: any, newValue: any) => handleChange('outputId', newValue?.id || '')}
              options={fluidOutputs?.data?.map((output: any) => ({
                value: output.id,
                label: output.name,
                id: output.id
              })) || []}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              disabled={outputsLoading}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Açıklama
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="Açıklama"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              Miktar (ml)
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="Miktar (ml)"
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12} container alignItems="center" justifyContent="space-between">
          <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
            <ByzMuiTypography sx={{ marginLeft: "30px" }}>
              İşlem Zamanı
            </ByzMuiTypography>
          </Grid>
          <Grid xs={12} sm={6} md={6}>
            <ByzMuiTextField
              label="İşlem Zamanı"
              type="datetime-local"
              value={formData.processTime}
              onChange={(e) => handleChange('processTime', e.target.value)}
              sx={{ width: "100%" }}
            />
          </Grid>
        </Grid>

        <Grid xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <ByzMuiSaveButton onClick={handleSave} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LiquidOutModal;